import React, { useEffect, useState, useId, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import { toast } from 'sonner';
import DatalistTextInput from './DatalistTextInput';
import { getOptionsFromRecords, resolveCountyForCity } from './countyMunicipalityData';
import { ensureCountyCreated, ensureMunicipalityCreated } from './creatableLocation';
import useMunicipalityCounty from '../../hooks/useMunicipalityCounty';

const fieldTitle = (label, required) => (
    <h6 className="midFont mb-1">
        {label}
        {required ? <span className="text-danger">*</span> : null}
    </h6>
);

const CountyMunicipalityDatalistFields = ({
    variant = 'countyAndMunicipality',
    initialCounty = '',
    initialCity = '',
    countyRequired = false,
    cityRequired = false,
    creatable = false,
    layout = 'stack',
    columnOrder = 'cityFirst',
    onFormChange,
    cityFieldLabel = 'City',
    countyFieldLabel = 'County',
    inputClassName = 'county-input-custom',
    disabled = false
}) => {
    const reactId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
    const listCounty = `cc-dl-county-${reactId}`;
    const listCity = `cc-dl-city-${reactId}`;

    const { records, loading, mergeRecord } = useMunicipalityCounty();
    const [persisting, setPersisting] = useState(false);
    const [county, setCounty] = useState(initialCounty || '');
    const [city, setCity] = useState(initialCity || '');

    useEffect(() => {
        setCounty(initialCounty || '');
        if (initialCity && !initialCounty) {
            const resolvedCounty = resolveCountyForCity(records, initialCity);
            if (resolvedCounty) {
                setCounty(resolvedCounty);
            }
        }
        setCity(initialCity || '');
    }, [initialCounty, initialCity]);

    const { countyNames, municipalityNames } = getOptionsFromRecords(records);

    const allowCreate = creatable === true || creatable === 'true';

    /** Save only on Enter — not on blur (typing then clicking away should not auto-save). */
    const persistCounty = useCallback(async () => {
        if (!allowCreate || persisting) return;
        const name = county.trim();
        if (!name) return;
        setPersisting(true);
        try {
            const result = await ensureCountyCreated(name, records);
            if (result.error) {
                toast.error(result.error || 'Could not save county');
                return;
            }
            if (result.record) {
                mergeRecord(result.record);
                toast.success('County saved successfully.');
            }
        } finally {
            setPersisting(false);
        }
    }, [allowCreate, persisting, county, records, mergeRecord]);

    const persistCity = useCallback(async () => {
        if (!allowCreate || persisting) return;
        const cityVal = city.trim();
        const countyVal = county.trim();
        if (!cityVal) return;
        if (!countyVal) {
            toast.error('Enter or select a county before entering a municipality (city).');
            return;
        }
        setPersisting(true);
        try {
            const result = await ensureMunicipalityCreated(countyVal, cityVal, records);
            if (result.error) {
                toast.error(result.error || 'Could not save municipality');
                return;
            }
            if (result.record) {
                mergeRecord(result.record);
                toast.success('Municipality saved successfully.');
            }
        } finally {
            setPersisting(false);
        }
    }, [allowCreate, persisting, city, county, records, mergeRecord]);

    const handleCountyChange = e => {

        const next = e.target.value;
        setCounty(next);
        if (onFormChange) onFormChange({ county: next, city });
    };



    const handleCityChange = e => {
        const v = e.target.value;
        setCity(v);
        const resolvedCounty = resolveCountyForCity(records, v);
        if (resolvedCounty) {
            setCounty(resolvedCounty);
        }
        if (onFormChange) {
            onFormChange({
                county: resolvedCounty ?? county,
                city: v
            });
        }
    };

    const handleCountyKeyDown = e => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        e.stopPropagation();
        persistCounty();
    };

    const handleCityKeyDown = e => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        e.stopPropagation();
        if (!county.trim()) {
            toast.error('Enter or select a county before entering a municipality (city).');
            return;
        }
        persistCity();
    };

    const disabledInput = disabled || loading || persisting;

    if (variant === 'countyOnly') {
        return (
            <>
                <input type="hidden" id="City" name="City" value="" readOnly onChange={() => { }} />
                {fieldTitle(countyFieldLabel, countyRequired)}
                <DatalistTextInput
                    id="County"
                    listId={listCounty}
                    value={county}
                    onChange={e => {

                        const next = e.target.value;
                        setCounty(next);
                        if (onFormChange) onFormChange({ county: next, city: '' });

                    }}
                    onKeyDown={handleCountyKeyDown}
                    options={countyNames}
                    placeholder="Type or choose county…"
                    disabled={disabledInput}
                    required={countyRequired}
                    className={inputClassName}
                    style={{ width: '100%' }}
                    aria-label={countyFieldLabel}
                />
            </>
        );
    }

    const cityBlock = (
        <>
            {fieldTitle(cityFieldLabel, cityRequired)}
            <DatalistTextInput
                id="City"
                listId={listCity}
                value={city}
                onChange={handleCityChange}
                onKeyDown={handleCityKeyDown}
                options={municipalityNames}
                placeholder="Type or choose city/municipality…"
                disabled={disabledInput}
                required={cityRequired}
                className={inputClassName}
                style={{ width: '100%' }}
                aria-label={cityFieldLabel}
            />
        </>
    );

    const countyBlock = (
        <>
            {fieldTitle(countyFieldLabel, countyRequired)}
            <DatalistTextInput
                id="County"
                listId={listCounty}
                value={county}
                onChange={handleCountyChange}
                onKeyDown={handleCountyKeyDown}
                options={countyNames}
                placeholder="Type or choose county…"
                disabled={disabledInput}
                required={countyRequired}
                className={inputClassName}
                style={{ width: '100%' }}
                aria-label={countyFieldLabel}
            />
        </>
    );

    if (layout === 'row') {
        const first = columnOrder === 'countyFirst' ? countyBlock : cityBlock;
        const second = columnOrder === 'countyFirst' ? cityBlock : countyBlock;
        return (
            <Row className="mx-0 w-100 align-items-end">
                <Col xs={12} md={6} className="pl-0 pr-md-2">
                    {first}
                </Col>
                <Col xs={12} md={6} className="pr-0 pl-md-2">
                    {second}
                </Col>
            </Row>
        );
    }

    return columnOrder === 'countyFirst' ? (
        <>
            <div className="mb-2">{countyBlock}</div>
            <div>{cityBlock}</div>
        </>
    ) : (
        <>
            <div className="mb-2">{cityBlock}</div>
            <div>{countyBlock}</div>
        </>
    );
};

export default CountyMunicipalityDatalistFields;
